import React, { Fragment, useState } from "react";
import { arrayOf, shape, string, func, object, bool } from "prop-types";
import { Link } from "react-router-dom";
import ReadingStatus from "../ReadingListDownload/reading_status";
import * as styles from "./styles.module.scss";
import * as style from "../ReadingListDownload/styles.module.scss";
import OnboardingSection from "../../common/OnboardingSectionCreator/OnboardingSection";
import {
  forceDownload,
  isDownloadAttrSupported,
} from "../../containers/ResourceFolders/util";
import {
  downloadResourcePdf,
  resourcePdfUrl,
  submitResourceAgreement,
} from "../../api/profileApi";
import Spinner from "../../common/Spinner/index";

const filenameFromUrl = (url) => url.split("/").slice(-1)[0];

const CpiOnboarding = ({
  appData,
  readingList,
  cpiOnboarding,
  markDocumentRead,
  cpiReadingLoading,
  setManualHandlingSubmitted,
}) => {
  const [currentlyReading, setCurrentlyReading] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);

  const resourcePdfDownload = async (formName, filename, complete = false) => {
    if (isDownloadAttrSupported()) {
      return downloadResourcePdf(formName, complete).then((response) =>
        forceDownload(response, filename)
      );
    }
    return window.open(resourcePdfUrl(formName, complete), "_blank");
  };

  const downloadPDFFile = async (complete) =>
    resourcePdfDownload(
      "getManualHandlingPDF",
      "Manual handling - lifting safely.pdf",
      complete
    );

  const downloadHandler = async () => {
    const fileFound = appData.manualHandling;

    if (!fileFound && !agreed) {
      setLoading(true);
      await downloadPDFFile(false);
      setOpenModal(true);
      setLoading(false);
    } else {
      setLoading(true);
      await downloadPDFFile(true);
      setLoading(false);
    }
  };

  const submitResource = async (formName, name, filename) => {
    return submitResourceAgreement(name, formName).then((response) => {
      if (isDownloadAttrSupported()) {
        return forceDownload(response, filename);
      }
      return window.open(resourcePdfUrl(formName, true), "_blank");
    });
  };

  const submitAgreementResource = async () => {
    setManualHandlingSubmitted();
    return submitResource(
      "manualAgreement",
      `${appData.firstName} ${appData.lastName}`,
      "Manual Handling - Lifting Safely.pdf"
    );
  };

  const acceptHandler = async () => {
    setLoading(true);
    setAgreed(true);
    await submitAgreementResource();
    setOpenModal(false);
    setLoading(false);
  };

  const readDocuments = (cpiOnboarding && cpiOnboarding.readDocuments) || [];

  const readingListWithStatus = readingList
    .map((download) => ({
      ...download,
      status: readDocuments.includes(filenameFromUrl(download.link))
        ? ReadingStatus.Done
        : ReadingStatus.Todo,
    }))
    .sort((a, b) => a.title.localeCompare(b.title));

  return (
    <div className="container text-bluishGray pb-2">
      <header className="row">
        <div className="col-12">
          <h2 className="section-header pt-2">CPI Onboarding</h2>
        </div>
      </header>
      <div className="row">
        <div className="col-lg-6 pb-1">
          <OnboardingSection
            title="Reading List"
            subtext="You are required to download and read the handbook below before you start work.
             You will then be asked to tick the box to confirm you have read and understood the handbook.
              If you have any queries, please speak to your line manager."
            readingListWithStatus={readingListWithStatus}
            currentlyReading={currentlyReading}
            setCurrentlyReading={setCurrentlyReading}
            markDocumentRead={markDocumentRead}
            isLoading={cpiReadingLoading}
            submit={() => {}}
          />
        </div>
        <div className="col-lg-6 pb-1">
          <div
            className={`${styles.manualHandling__wrapper} section-body pt-1 bg-white`}
          >
            <h5 className="px-1">Manual Handling</h5>
            <p className="p-1" style={{ width: "90%" }}>
              Please download this form and read it fully, you do not have to
              complete the fields. Once you have ticked the box to confirm you
              have read and understood the form and agree to the declaration, we
              will complete the fields on the form e.g. name, signature, date,
              for you. If you have any queries about the form, please speak to
              your HRGO consultant.
            </p>
            <div className={`${styles.mhdownload__wrapper}`}>
              <div className={`${styles.mhdownload__mobile_row}`}>
                <div
                  className={`${styles.mhdownload__status} ${
                    appData.manualHandling
                      ? styles.mhdownload__done
                      : styles.mhdownload__todo
                  }`}
                >
                  {appData.manualHandling ? "Done" : "To Do"}
                </div>
                <span className={`${styles.mhdownload__title}`}>
                  Manual Handling
                </span>
              </div>
              <button
                disabled={loading}
                style={openModal ? { marginBottom: "15px" } : null}
                className={`${style.rldownload__button} ${style.rldownload__download} btn btn-md btn-outline`}
                onClick={() => downloadHandler()}
              >
                {loading ? <Spinner /> : "Download"}
              </button>
            </div>
            {openModal ? (
              <div className={`${styles.mhdownload__agreementWrapper}`}>
                <div className="d-flex flex-column">
                  <div
                    style={{
                      display: "flex",
                      flexFlow: "row",
                    }}
                  >
                    <div className={styles.mhdownload__agreement}>
                      <input
                        style={{ marginRight: "10px" }}
                        type="checkbox"
                        name="document_read"
                        onClick={() => acceptHandler()}
                      />
                      <span>
                        I have read and understood the Lifting Safely advice and
                        will adhere to the summary stated in this document.
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setAgreed(false);
                    setOpenModal(false);
                  }}
                  className={`${styles.mhcancel__button} btn btn-md btn-outline mx-1 mx-sm-0`}
                >
                  Cancel
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      <div className="d-flex justify-content-end mt-4">
        <Link to="/profile" className="btn btn-primary px-3">
          Back to My Account
        </Link>
      </div>
    </div>
  );
};

CpiOnboarding.propTypes = {
  markDocumentRead: func.isRequired,
  cpiOnboarding: object,
  cpiReadingLoading: bool,
  setManualHandlingSubmitted: func,
  readingList: arrayOf(
    shape({
      title: string.isRequired,
      link: string.isRequired,
      agreementText: string.isRequired,
    })
  ),
};

export default CpiOnboarding;
